import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Mail, Send, Search, Users, User, Paperclip, X, Loader2, 
  CheckCircle, AlertCircle, MailOpen, RefreshCw, ChevronDown,
  Bold, Italic, Underline, Link, List, ListOrdered, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminMensajesPage() {
  const { token } = useAuth();
  const [tab, setTab] = useState('bandeja'); // 'bandeja', 'enviar', 'enviados' - Bandeja por defecto
  
  // Estado para envío
  const [tipoEnvio, setTipoEnvio] = useState('individual'); // 'individual' o 'masivo'
  const [busqueda, setBusqueda] = useState('');
  const [resultadosBusqueda, setResultadosBusqueda] = useState([]);
  const [destinatarioSeleccionado, setDestinatarioSeleccionado] = useState(null);
  const [asunto, setAsunto] = useState('');
  const [contenido, setContenido] = useState('');
  const [adjuntos, setAdjuntos] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [buscando, setBuscando] = useState(false);
  
  // Filtros para envío masivo
  const [localidadesReal, setLocalidadesReal] = useState([]);
  const [localidadesLaboral, setLocalidadesLaboral] = useState([]);
  const [filtroLocalidadReal, setFiltroLocalidadReal] = useState('');
  const [filtroLocalidadLaboral, setFiltroLocalidadLaboral] = useState('');
  
  // Estado para mensajes enviados
  const [mensajesEnviados, setMensajesEnviados] = useState([]);
  const [loadingEnviados, setLoadingEnviados] = useState(false);
  
  // Estado para bandeja de entrada
  const [bandejaEntrada, setBandejaEntrada] = useState({ mensajes_usuarios: [], mensajes_con_respuestas: [] });
  const [loadingBandeja, setLoadingBandeja] = useState(false);
  
  // Mensaje seleccionado para ver detalle
  const [mensajeDetalle, setMensajeDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [respuestaAdmin, setRespuestaAdmin] = useState('');
  
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  // Cargar localidades para filtros de envío masivo
  useEffect(() => {
    const fetchLocalidades = async () => {
      try {
        const [resReal, resLaboral] = await Promise.all([
          fetch(`${API_URL}/api/mensajes-internos/admin/localidades?tipo=real`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/mensajes-internos/admin/localidades?tipo=laboral`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        if (resReal.ok) setLocalidadesReal(await resReal.json());
        if (resLaboral.ok) setLocalidadesLaboral(await resLaboral.json());
      } catch (err) {
        console.error('Error cargando localidades:', err);
      }
    };
    fetchLocalidades();
  }, [token]);

  // Buscar usuarios
  const buscarUsuarios = useCallback(async (query) => {
    if (query.length < 2) {
      setResultadosBusqueda([]);
      return;
    }
    setBuscando(true);
    try {
      const res = await fetch(`${API_URL}/api/mensajes-internos/admin/buscar-usuarios?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setResultadosBusqueda(data);
      }
    } catch (err) {
      console.error('Error buscando usuarios:', err);
    } finally {
      setBuscando(false);
    }
  }, [token]);

  useEffect(() => {
    const timeout = setTimeout(() => buscarUsuarios(busqueda), 300);
    return () => clearTimeout(timeout);
  }, [busqueda, buscarUsuarios]);

  // Cargar mensajes enviados
  const fetchMensajesEnviados = async () => {
    setLoadingEnviados(true);
    try {
      const res = await fetch(`${API_URL}/api/mensajes-internos/admin/enviados`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMensajesEnviados(data.mensajes || []);
      }
    } catch (err) {
      toast.error('Error al cargar mensajes');
    } finally {
      setLoadingEnviados(false);
    }
  };

  // Cargar bandeja de entrada
  const fetchBandejaEntrada = async () => {
    setLoadingBandeja(true);
    try {
      const res = await fetch(`${API_URL}/api/mensajes-internos/admin/bandeja-entrada`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBandejaEntrada(data);
      }
    } catch (err) {
      toast.error('Error al cargar bandeja');
    } finally {
      setLoadingBandeja(false);
    }
  };

  useEffect(() => {
    if (tab === 'enviados') fetchMensajesEnviados();
    if (tab === 'bandeja') fetchBandejaEntrada();
  }, [tab]);

  // Editor de texto rico - comandos básicos
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const insertLink = () => {
    const url = prompt('Ingresa la URL del enlace:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  // Upload de archivos
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/mensajes-internos/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Error al subir archivo');
      }
      
      const data = await res.json();
      setAdjuntos(prev => [...prev, {
        nombre: data.nombre,
        url: data.url,
        tipo: data.tipo,
        size: data.size
      }]);
      toast.success('Archivo adjuntado');
    } catch (err) {
      toast.error(err.message);
    }
    e.target.value = '';
  };

  // Enviar mensaje
  const enviarMensaje = async () => {
    const contenidoHtml = editorRef.current?.innerHTML || '';
    
    if (!contenidoHtml.trim()) {
      toast.error('El mensaje no puede estar vacío');
      return;
    }
    
    if (tipoEnvio === 'individual' && !destinatarioSeleccionado) {
      toast.error('Selecciona un destinatario');
      return;
    }
    
    setEnviando(true);
    try {
      const payload = {
        asunto: asunto,
        contenido: contenidoHtml,
        adjuntos: adjuntos,
        es_masivo: tipoEnvio === 'masivo',
        destinatario_id: tipoEnvio === 'individual' ? destinatarioSeleccionado.usuario_id : null,
        filtro_localidad_real: filtroLocalidadReal || null,
        filtro_localidad_laboral: filtroLocalidadLaboral || null
      };
      
      const res = await fetch(`${API_URL}/api/mensajes-internos/admin/enviar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Error al enviar mensaje');
      }
      
      const data = await res.json();
      toast.success(`Mensaje enviado a ${data.destinatarios} destinatario(s)`);
      
      // Limpiar formulario
      setAsunto('');
      setContenido('');
      setAdjuntos([]);
      setDestinatarioSeleccionado(null);
      setBusqueda('');
      if (editorRef.current) editorRef.current.innerHTML = '';
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setEnviando(false);
    }
  };

  // Ver detalle de mensaje
  const verDetalleMensaje = async (mensajeId) => {
    setLoadingDetalle(true);
    try {
      const res = await fetch(`${API_URL}/api/mensajes-internos/admin/mensaje/${mensajeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMensajeDetalle(data);
      }
    } catch (err) {
      toast.error('Error al cargar mensaje');
    } finally {
      setLoadingDetalle(false);
    }
  };

  // Responder mensaje
  const responderMensaje = async () => {
    if (!respuestaAdmin.trim() || !mensajeDetalle) return;
    
    try {
      // Determinar el endpoint correcto según el tipo de mensaje
      const esDeUsuario = mensajeDetalle.tipo === 'usuario_a_admin';
      const endpoint = esDeUsuario 
        ? `${API_URL}/api/mensajes-internos/admin/responder-mensaje-usuario/${mensajeDetalle.mensaje.id}`
        : `${API_URL}/api/mensajes-internos/admin/mensaje/${mensajeDetalle.mensaje.id}/responder`;
      
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ contenido: respuestaAdmin, adjuntos: [] })
      });
      
      if (res.ok) {
        toast.success('Respuesta enviada');
        setRespuestaAdmin('');
        
        // Recargar el detalle según el tipo
        if (esDeUsuario) {
          // Recargar respuestas del mensaje de usuario
          const respuestasRes = await fetch(
            `${API_URL}/api/mensajes-internos/admin/mensaje-usuario/${mensajeDetalle.mensaje.id}/respuestas`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (respuestasRes.ok) {
            const respuestas = await respuestasRes.json();
            setMensajeDetalle(prev => ({ ...prev, respuestas }));
          }
        } else {
          verDetalleMensaje(mensajeDetalle.mensaje.id);
        }
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.detail || 'Error al enviar respuesta');
      }
    } catch (err) {
      console.error('Error enviando respuesta:', err);
      toast.error('Error al enviar respuesta');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Limpiar HTML para mostrar preview
  const limpiarHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]+>/g, '').substring(0, 150);
  };

  // Ver detalle de mensaje de usuario
  const verDetalleMensajeUsuario = async (msg) => {
    setMensajeDetalle({
      mensaje: msg,
      respuestas: [],
      tipo: 'usuario_a_admin'
    });
    
    // Cargar respuestas existentes
    try {
      const respuestasRes = await fetch(
        `${API_URL}/api/mensajes-internos/admin/mensaje-usuario/${msg.id}/respuestas`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (respuestasRes.ok) {
        const respuestas = await respuestasRes.json();
        setMensajeDetalle(prev => ({ ...prev, respuestas }));
      }
    } catch (err) {
      console.error('Error cargando respuestas:', err);
    }
    
    // Marcar como leído
    try {
      await fetch(`${API_URL}/api/mensajes-internos/admin/marcar-leido/${msg.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      // Actualizar la lista
      fetchBandejaEntrada();
    } catch (err) {
      console.error('Error marcando como leído:', err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Mail className="w-6 h-6" />
          Mensajería Interna
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => { setTab('bandeja'); setMensajeDetalle(null); }}
          className={`px-4 py-2 font-medium transition-colors relative ${
            tab === 'bandeja' 
              ? 'text-primary border-b-2 border-primary' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Mail className="w-4 h-4 inline mr-2" />
          Bandeja de Entrada
          {bandejaEntrada.respuestas_no_leidas > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {bandejaEntrada.respuestas_no_leidas}
            </span>
          )}
        </button>
        <button
          onClick={() => { setTab('enviar'); setMensajeDetalle(null); }}
          className={`px-4 py-2 font-medium transition-colors ${
            tab === 'enviar' 
              ? 'text-primary border-b-2 border-primary' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Send className="w-4 h-4 inline mr-2" />
          Enviar Mensaje
        </button>
        <button
          onClick={() => { setTab('enviados'); setMensajeDetalle(null); }}
          className={`px-4 py-2 font-medium transition-colors ${
            tab === 'enviados' 
              ? 'text-primary border-b-2 border-primary' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <MailOpen className="w-4 h-4 inline mr-2" />
          Enviados
        </button>
      </div>

      {/* ============ TAB: ENVIAR MENSAJE ============ */}
      {tab === 'enviar' && (
        <Card>
          <CardHeader>
            <CardTitle>Nuevo Mensaje</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Tipo de envío */}
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="tipoEnvio"
                  value="individual"
                  checked={tipoEnvio === 'individual'}
                  onChange={() => setTipoEnvio('individual')}
                  className="w-4 h-4"
                />
                <User className="w-4 h-4" />
                Individual
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="tipoEnvio"
                  value="masivo"
                  checked={tipoEnvio === 'masivo'}
                  onChange={() => setTipoEnvio('masivo')}
                  className="w-4 h-4"
                />
                <Users className="w-4 h-4" />
                Masivo
              </label>
            </div>

            {/* Destinatario individual */}
            {tipoEnvio === 'individual' && (
              <div className="space-y-2">
                <Label>Buscar destinatario</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="Buscar por nombre, matrícula, DNI o email..."
                    className="pl-10"
                  />
                  {buscando && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" />
                  )}
                </div>
                
                {/* Resultados de búsqueda */}
                {resultadosBusqueda.length > 0 && !destinatarioSeleccionado && (
                  <div className="border rounded-md max-h-48 overflow-y-auto">
                    {resultadosBusqueda.map((u) => (
                      <button
                        key={u.usuario_id}
                        onClick={() => {
                          setDestinatarioSeleccionado(u);
                          setBusqueda('');
                          setResultadosBusqueda([]);
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
                      >
                        <span>{u.nombre}</span>
                        <span className="text-sm text-gray-500">MP: {u.matricula}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Destinatario seleccionado */}
                {destinatarioSeleccionado && (
                  <div className="flex items-center gap-2 p-2 bg-green-50 rounded-md">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="font-medium">{destinatarioSeleccionado.nombre}</span>
                    <span className="text-sm text-gray-500">({destinatarioSeleccionado.email})</span>
                    <button 
                      onClick={() => setDestinatarioSeleccionado(null)}
                      className="ml-auto text-gray-500 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Filtros masivo */}
            {tipoEnvio === 'masivo' && (
              <div className="grid grid-cols-2 gap-4 p-4 bg-yellow-50 rounded-md">
                <div>
                  <Label>Filtrar por localidad (domicilio real)</Label>
                  <select
                    value={filtroLocalidadReal}
                    onChange={(e) => setFiltroLocalidadReal(e.target.value)}
                    className="w-full mt-1 p-2 border rounded-md"
                  >
                    <option value="">Todas las localidades</option>
                    {localidadesReal.map((loc) => (
                      <option key={loc} value={loc}>{loc}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Filtrar por localidad (domicilio laboral)</Label>
                  <select
                    value={filtroLocalidadLaboral}
                    onChange={(e) => setFiltroLocalidadLaboral(e.target.value)}
                    className="w-full mt-1 p-2 border rounded-md"
                  >
                    <option value="">Todas las localidades</option>
                    {localidadesLaboral.map((loc) => (
                      <option key={loc} value={loc}>{loc}</option>
                    ))}
                  </select>
                </div>
                <p className="col-span-2 text-sm text-yellow-700">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  El mensaje se enviará a todos los usuarios registrados que coincidan con los filtros.
                </p>
              </div>
            )}

            {/* Asunto */}
            <div>
              <Label>Asunto (opcional)</Label>
              <Input
                value={asunto}
                onChange={(e) => setAsunto(e.target.value)}
                placeholder="Asunto del mensaje..."
              />
            </div>

            {/* Editor de texto rico */}
            <div>
              <Label>Mensaje</Label>
              <div className="border rounded-md overflow-hidden">
                {/* Toolbar */}
                <div className="flex items-center gap-1 p-2 bg-gray-50 border-b flex-wrap">
                  <button
                    onClick={() => execCommand('bold')}
                    className="p-2 hover:bg-gray-200 rounded"
                    title="Negrita"
                  >
                    <Bold className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => execCommand('italic')}
                    className="p-2 hover:bg-gray-200 rounded"
                    title="Cursiva"
                  >
                    <Italic className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => execCommand('underline')}
                    className="p-2 hover:bg-gray-200 rounded"
                    title="Subrayado"
                  >
                    <Underline className="w-4 h-4" />
                  </button>
                  <div className="w-px h-6 bg-gray-300 mx-1" />
                  <button
                    onClick={() => execCommand('insertUnorderedList')}
                    className="p-2 hover:bg-gray-200 rounded"
                    title="Lista"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => execCommand('insertOrderedList')}
                    className="p-2 hover:bg-gray-200 rounded"
                    title="Lista numerada"
                  >
                    <ListOrdered className="w-4 h-4" />
                  </button>
                  <div className="w-px h-6 bg-gray-300 mx-1" />
                  <button
                    onClick={insertLink}
                    className="p-2 hover:bg-gray-200 rounded"
                    title="Insertar enlace"
                  >
                    <Link className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Editor */}
                <div
                  ref={editorRef}
                  contentEditable
                  className="min-h-[200px] p-4 focus:outline-none prose prose-sm max-w-none"
                  onInput={(e) => setContenido(e.currentTarget.innerHTML)}
                  data-placeholder="Escribe tu mensaje aquí..."
                />
              </div>
            </div>

            {/* Adjuntos */}
            <div>
              <Label>Archivos adjuntos</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {adjuntos.map((adj, idx) => (
                  <div key={idx} className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full text-sm">
                    <Paperclip className="w-3 h-3" />
                    <span className="truncate max-w-[150px]">{adj.nombre}</span>
                    <button 
                      onClick={() => setAdjuntos(prev => prev.filter((_, i) => i !== idx))}
                      className="text-gray-500 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <label className="cursor-pointer">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                  <span className="flex items-center gap-2 px-3 py-1 border border-dashed rounded-full text-sm text-gray-600 hover:bg-gray-50">
                    <Paperclip className="w-3 h-3" />
                    Adjuntar archivo
                  </span>
                </label>
              </div>
            </div>

            {/* Botón enviar */}
            <div className="flex justify-end">
              <Button 
                onClick={enviarMensaje} 
                disabled={enviando}
                className="gap-2"
              >
                {enviando ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {tipoEnvio === 'masivo' ? 'Enviar a todos' : 'Enviar mensaje'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ============ TAB: MENSAJES ENVIADOS ============ */}
      {tab === 'enviados' && !mensajeDetalle && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Mensajes Enviados</CardTitle>
            <Button variant="outline" size="sm" onClick={fetchMensajesEnviados}>
              <RefreshCw className={`w-4 h-4 ${loadingEnviados ? 'animate-spin' : ''}`} />
            </Button>
          </CardHeader>
          <CardContent>
            {loadingEnviados ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : mensajesEnviados.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No hay mensajes enviados</p>
            ) : (
              <div className="divide-y">
                {mensajesEnviados.map((msg) => (
                  <div
                    key={msg.id}
                    onClick={() => verDetalleMensaje(msg.id)}
                    className="p-4 cursor-pointer hover:bg-gray-50"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {msg.tipo === 'masivo' ? (
                          <Users className="w-4 h-4 text-purple-500" />
                        ) : (
                          <User className="w-4 h-4 text-blue-500" />
                        )}
                        <span className="font-medium">
                          {msg.asunto || 'Sin asunto'}
                        </span>
                      </div>
                      <span className="text-sm text-gray-500">
                        {formatDate(msg.created_at)}
                      </span>
                    </div>
                    {msg.estadisticas && (
                      <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                        <span>
                          <Users className="w-3 h-3 inline mr-1" />
                          {msg.estadisticas.total} destinatarios
                        </span>
                        <span>
                          <MailOpen className="w-3 h-3 inline mr-1" />
                          {msg.estadisticas.leidos} leídos ({msg.estadisticas.porcentaje}%)
                        </span>
                        {msg.tiene_respuestas_nuevas && (
                          <span className="text-orange-500 font-medium">
                            • Tiene respuestas nuevas
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ============ TAB: BANDEJA DE ENTRADA ============ */}
      {tab === 'bandeja' && !mensajeDetalle && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Bandeja de Entrada</CardTitle>
            <Button variant="outline" size="sm" onClick={fetchBandejaEntrada}>
              <RefreshCw className={`w-4 h-4 ${loadingBandeja ? 'animate-spin' : ''}`} />
            </Button>
          </CardHeader>
          <CardContent>
            {loadingBandeja ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Mensajes con respuestas (hilos) */}
                {bandejaEntrada.mensajes_con_respuestas?.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-700 mb-2">
                      Conversaciones con usuarios
                      <span className="text-sm font-normal text-gray-500 ml-2">
                        ({bandejaEntrada.mensajes_con_respuestas.length} hilos)
                      </span>
                    </h3>
                    <div className="divide-y border rounded-md">
                      {bandejaEntrada.mensajes_con_respuestas.map((msg) => (
                        <div
                          key={msg.id}
                          onClick={() => verDetalleMensaje(msg.id)}
                          className={`p-4 cursor-pointer hover:bg-gray-50 ${
                            msg.tiene_respuestas_no_leidas 
                              ? 'bg-orange-50 border-l-4 border-l-orange-500' 
                              : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {msg.tiene_respuestas_no_leidas && (
                                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                              )}
                              <span className="font-medium">{msg.asunto || 'Sin asunto'}</span>
                              {msg.respuestas_no_leidas > 0 && (
                                <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full">
                                  {msg.respuestas_no_leidas} nueva{msg.respuestas_no_leidas > 1 ? 's' : ''}
                                </span>
                              )}
                              {msg.total_respuestas > 0 && !msg.tiene_respuestas_no_leidas && (
                                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                                  {msg.total_respuestas} respuesta{msg.total_respuestas > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                            <span className="text-sm text-gray-500">{formatDate(msg.created_at)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mensajes de usuarios */}
                {bandejaEntrada.mensajes_usuarios?.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-700 mb-2">
                      Mensajes de usuarios 
                      {bandejaEntrada.total_mensajes > 0 && (
                        <span className="text-sm font-normal text-gray-500 ml-2">
                          ({bandejaEntrada.total_mensajes} total)
                        </span>
                      )}
                    </h3>
                    <div className="divide-y border rounded-md">
                      {bandejaEntrada.mensajes_usuarios.map((msg) => (
                        <div
                          key={msg.id}
                          className={`p-4 cursor-pointer hover:bg-gray-50 ${!msg.leido_por_admin ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}
                          onClick={() => verDetalleMensajeUsuario(msg)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {!msg.leido_por_admin && (
                                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                              )}
                              <span className="font-medium">{msg.remitente_nombre || msg.remitente_email}</span>
                              {msg.remitente_matricula && (
                                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                                  MP: {msg.remitente_matricula}
                                </span>
                              )}
                            </div>
                            <span className="text-sm text-gray-500">{formatDate(msg.created_at)}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2 ml-4">{limpiarHtml(msg.contenido)}</p>
                          {msg.remitente_email && msg.remitente_nombre !== msg.remitente_email && (
                            <p className="text-xs text-gray-400 mt-1 ml-4">{msg.remitente_email}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {bandejaEntrada.mensajes_con_respuestas?.length === 0 && 
                 bandejaEntrada.mensajes_usuarios?.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No hay mensajes en la bandeja</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ============ DETALLE DE MENSAJE ============ */}
      {mensajeDetalle && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={() => setMensajeDetalle(null)}>
                ← Volver
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingDetalle ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                <div className="border-b pb-4">
                  <h2 className="text-xl font-semibold">{mensajeDetalle.mensaje.asunto || 'Sin asunto'}</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Enviado: {formatDate(mensajeDetalle.mensaje.created_at)}
                    {mensajeDetalle.mensaje.metadata && (
                      <span className="ml-2">
                        • {mensajeDetalle.mensaje.metadata.total_destinatarios} destinatarios
                        • {mensajeDetalle.mensaje.metadata.total_leidos || 0} leídos
                      </span>
                    )}
                  </p>
                </div>

                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: mensajeDetalle.mensaje.contenido }}
                />

                {/* Respuestas */}
                {mensajeDetalle.respuestas?.length > 0 && (
                  <div className="border-t pt-4 space-y-3">
                    <h3 className="font-medium">Respuestas</h3>
                    {mensajeDetalle.respuestas.map((resp) => (
                      <div 
                        key={resp.id}
                        className={`p-3 rounded-lg ${
                          resp.remitente_tipo === 'admin' ? 'bg-gray-100 ml-8' : 'bg-blue-50 mr-8'
                        }`}
                      >
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="font-medium">
                            {resp.remitente_tipo === 'admin' ? 'Administración' : resp.remitente_nombre || resp.remitente_email}
                          </span>
                          <span className="text-gray-500">{formatDate(resp.created_at)}</span>
                        </div>
                        <div dangerouslySetInnerHTML={{ __html: resp.contenido }} />
                      </div>
                    ))}
                  </div>
                )}

                {/* Responder */}
                <div className="border-t pt-4">
                  <Label>Responder</Label>
                  <textarea
                    value={respuestaAdmin}
                    onChange={(e) => setRespuestaAdmin(e.target.value)}
                    className="w-full p-3 border rounded-md mt-2"
                    rows={3}
                    placeholder="Escribe tu respuesta..."
                  />
                  <div className="flex justify-end mt-2">
                    <Button onClick={responderMensaje} disabled={!respuestaAdmin.trim()}>
                      <Send className="w-4 h-4 mr-2" />
                      Enviar respuesta
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
